<template>
  <div class="metharct-beta-container">
    <div class="content-section">
      <h1>MethArCT-Beta Comprehensive Analysis</h1>

      <div class="description">
        <h2>About This Tool</h2>
        <p>
          MethArCT-Beta integrates Diamond metabolic pathway analysis, Tome OGT prediction,
          SuSha salinity prediction, pH preference prediction, antibiotic resistance prediction,
          and cultivation pathway analysis into a single comprehensive workflow.
        </p>
        <p style="color: #4169E1; font-weight: 500;">
          This tool is based on MethArCT v0.6.0 with improvements in comprehensive analysis integration.
        </p>
        <el-alert title="Processing Time Notice" type="info" :closable="false" show-icon>
          Comprehensive analysis may take 3–10 minutes depending on file size.
        </el-alert>
      </div>

      <!-- Upload -->
      <div class="upload-section" v-if="!results">
        <h2>Upload Protein Sequence File</h2>
        <p class="upload-info">Supported: .fa, .fasta, .faa (max 50 MB)</p>
        <el-upload
          drag action="/metharctapi/tools/metharct-beta/upload/"
          :on-success="handleUploadSuccess"
          :on-error="handleUploadError"
          :before-upload="beforeUpload"
          :limit="1"
          :file-list="fileList"
        >
          <el-icon class="el-icon--upload"><upload-filled /></el-icon>
          <div class="el-upload__text">Drop file here or <em>click to upload</em></div>
        </el-upload>

        <div v-if="jobId && !analyzing" style="margin-top:20px;">
          <el-button type="primary" size="large" @click="startAnalysis" :loading="analyzing">
            Start Comprehensive Analysis
          </el-button>
        </div>

        <div v-if="analyzing" style="margin-top:20px;">
          <el-progress :percentage="100" status="warning" :indeterminate="true" :duration="3" />
          <p style="text-align:center;color:#666;margin-top:10px;">Running comprehensive analysis, please wait...</p>
        </div>
      </div>

      <!-- ===================== RESULTS ===================== -->
      <div v-if="results" class="results-section">
        <!-- Completion banner -->
        <el-alert
          :title="`Analysis Complete (${results.run_log?.elapsed || '?'}s)`"
          type="success"
          :closable="false"
          show-icon
          style="margin-bottom:24px;"
        >
          <span>Completed modules: {{ (results.run_log?.analyses_performed || []).join(', ') }}</span>
        </el-alert>

        <!-- New Analysis button -->
        <div style="text-align:right; margin-bottom:16px;">
          <el-button type="warning" @click="resetAnalysis">New Analysis</el-button>
        </div>

        <!-- 1. Overall Assessment -->
        <div class="result-card">
          <h3>Overall Assessment</h3>
          <div class="assessment-grid">
            <div class="assess-item"><span class="label">Organism Type:</span> {{ results.overall_assessment?.organism_type }}</div>
            <div class="assess-item"><span class="label">Metabolic Complexity:</span> {{ results.overall_assessment?.metabolic_complexity }}</div>
            <div class="assess-item"><span class="label">Cultivation Potential:</span> {{ results.overall_assessment?.cultivation_potential }}</div>
            <div class="assess-item"><span class="label">Confidence:</span> {{ results.overall_assessment?.confidence }}</div>
          </div>
          <div v-if="results.overall_assessment?.key_characteristics?.length" class="key-features">
            <strong>Key Characteristics:</strong>
            <el-tag v-for="(feat, i) in results.overall_assessment.key_characteristics" :key="i"
              style="margin:4px;" type="info">{{ feat }}</el-tag>
          </div>
        </div>

        <!-- 2. pH Prediction -->
        <div class="result-card" v-if="results.ph_prediction?.optimum">
          <h3>pH Preference Prediction</h3>
          <div class="metric-row">
            <div class="metric-box">
              <div class="metric-value">{{ results.ph_prediction.optimum }}</div>
              <div class="metric-label">Optimum pH</div>
            </div>
            <div class="metric-box">
              <div class="metric-value">{{ results.ph_prediction.max }}</div>
              <div class="metric-label">Max pH</div>
            </div>
            <div class="metric-box">
              <div class="metric-value">{{ results.ph_prediction.min }}</div>
              <div class="metric-label">Min pH</div>
            </div>
          </div>
        </div>

        <!-- 3. Temperature Prediction -->
        <div class="result-card" v-if="results.temperature_prediction?.optimal">
          <h3>Temperature Prediction</h3>
          <div class="metric-row">
            <div class="metric-box">
              <div class="metric-value temp">{{ results.temperature_prediction.optimal }}°C</div>
              <div class="metric-label">Optimal Growth Temp</div>
            </div>
            <div class="metric-box">
              <div class="metric-value">{{ results.temperature_prediction.max }}°C</div>
              <div class="metric-label">Max Growth Temp</div>
            </div>
            <div class="metric-box">
              <div class="metric-value">{{ results.temperature_prediction.min }}°C</div>
              <div class="metric-label">Min Growth Temp</div>
            </div>
          </div>
          <el-tag v-if="results.temperature_prediction.category" type="info" style="margin-top:8px;">
            {{ results.temperature_prediction.category }}
          </el-tag>
        </div>

        <!-- 4. Salinity -->
        <div class="result-card" v-if="results.salinity?.label && results.salinity.label !== 'Unknown'">
          <h3>Salinity Adaptation Prediction</h3>
          <div class="metric-row">
            <div class="metric-box">
              <div class="metric-value">{{ results.salinity.label }}</div>
              <div class="metric-label">Predicted Salinity Type</div>
            </div>
            <div class="metric-box">
              <div class="metric-value">{{ (results.salinity.confidence * 100).toFixed(1) }}%</div>
              <div class="metric-label">Confidence</div>
            </div>
          </div>
        </div>

        <!-- 5. Antibiotic Resistance -->
        <div class="result-card" v-if="results.antibiotic_result?.recommended?.length || results.antibiotic_result?.aai_table?.length">
          <h3>Antibiotic Resistance Prediction</h3>
          <div v-if="results.antibiotic_result.recommended.length">
            <strong>Recommended Antibiotics:</strong>
            <el-tag v-for="(ab, i) in results.antibiotic_result.recommended" :key="i" type="danger" style="margin:4px;">{{ ab }}</el-tag>
          </div>
          <el-table v-if="results.antibiotic_result.aai_table.length"
            :data="results.antibiotic_result.aai_table" border stripe style="margin-top:12px;">
            <el-table-column prop="reference" label="Reference Genome" />
            <el-table-column prop="aai" label="AAI(%)" width="120" />
          </el-table>
        </div>

        <!-- 6. Metabolic Pathway Tables -->
        <h3 style="margin-top:32px;">Metabolic Pathway Completeness Analysis</h3>
        <el-tabs type="border-card" v-model="pathwayTab">
          <!-- Methane -->
          <el-tab-pane label="Methane Metabolism" name="methane">
            <el-table :data="results.methane_table" border stripe>
              <el-table-column prop="pathway" label="Pathway" min-width="200" />
              <el-table-column label="Completeness" width="120" sortable :sort-method="(a,b)=>a.completeness-b.completeness">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">
                    {{ row.completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Gene Hits" width="140">
                <template #default="{ row }">{{ row.hits }}/{{ row.total }}</template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize: '18px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- Sulfur -->
          <el-tab-pane label="Sulfur Metabolism" name="sulfur">
            <el-table :data="results.sulfur_table" border stripe>
              <el-table-column prop="pathway" label="Pathway" min-width="200" />
              <el-table-column label="Completeness" width="120" sortable :sort-method="(a,b)=>a.completeness-b.completeness">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">
                    {{ row.completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Gene Hits" width="140">
                <template #default="{ row }">{{ row.hits }}/{{ row.total }}</template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize: '18px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- Nitrogen -->
          <el-tab-pane label="Nitrogen Metabolism" name="nitrogen">
            <el-table :data="results.nitrogen_table" border stripe>
              <el-table-column prop="pathway" label="Pathway" min-width="200" />
              <el-table-column label="Completeness" width="120" sortable :sort-method="(a,b)=>a.completeness-b.completeness">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">
                    {{ row.completeness }}%
                  </span>
                </template>
              </el-table-column>
              <el-table-column label="Gene Hits" width="140">
                <template #default="{ row }">{{ row.hits }}/{{ row.total }}</template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize: '18px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>

        <!-- 7. Cultivation Pathway Results -->
        <h3 style="margin-top:32px;" v-if="results.cultivation_results?.amino_acid?.summary?.total">
          Cultivation Pathway Analysis
        </h3>
        <el-tabs type="border-card" v-model="cultivationTab"
          v-if="results.cultivation_results?.amino_acid?.summary?.total">
          <!-- Amino acid -->
          <el-tab-pane label="Amino Acid Biosynthesis (20)" name="aa">
            <div class="aa-summary-bar">
              <span style="color:#67c23a;">Present: {{ results.cultivation_results.amino_acid.summary.present }}/{{ results.cultivation_results.amino_acid.summary.total }}</span>
              <span style="color:#f56c6c;margin-left:16px;">Missing/Partial: {{ results.cultivation_results.amino_acid.summary.missing_or_partial }}/{{ results.cultivation_results.amino_acid.summary.total }}</span>
            </div>
            <div v-if="results.cultivation_results.amino_acid.summary.missing_list?.length" style="margin:8px 0;">
              <strong style="color:#f56c6c;">Missing:</strong>
              <el-tag v-for="aa in results.cultivation_results.amino_acid.summary.missing_list" :key="aa" type="danger" style="margin:3px;">{{ aa }}</el-tag>
            </div>
            <el-table v-if="results.cultivation_results.amino_acid.categories?.length"
              :data="results.cultivation_results.amino_acid.categories" border stripe max-height="400">
              <el-table-column prop="name" label="Pathway" min-width="180" />
              <el-table-column label="Completeness" width="120">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">{{ row.completeness }}%</span>
                </template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize:'16px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- Vitamin -->
          <el-tab-pane label="Vitamin/Cofactor Biosynthesis" name="vc">
            <el-table v-if="results.cultivation_results.vitamin?.categories?.length"
              :data="results.cultivation_results.vitamin.categories" border stripe max-height="400">
              <el-table-column prop="name" label="Pathway" min-width="180" />
              <el-table-column label="Completeness" width="120">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">{{ row.completeness }}%</span>
                </template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize:'16px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="No data" />
          </el-tab-pane>

          <!-- Nucleotide -->
          <el-tab-pane label="Nucleotide Biosynthesis" name="nuc">
            <el-table v-if="results.cultivation_results.nucleotide?.categories?.length"
              :data="results.cultivation_results.nucleotide.categories" border stripe max-height="400">
              <el-table-column prop="name" label="Pathway" min-width="180" />
              <el-table-column label="Completeness" width="120">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">{{ row.completeness }}%</span>
                </template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize:'16px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="No data" />
          </el-tab-pane>

          <!-- ATP -->
          <el-tab-pane label="ATP Metabolism Pathways" name="atp">
            <el-table v-if="results.cultivation_results.atp?.categories?.length"
              :data="results.cultivation_results.atp.categories" border stripe max-height="400">
              <el-table-column prop="name" label="Pathway" min-width="180" />
              <el-table-column label="Completeness" width="120">
                <template #default="{ row }">
                  <span :style="{ color: row.completeness >= 80 ? '#67c23a' : '#e6a23c' }">{{ row.completeness }}%</span>
                </template>
              </el-table-column>
              <el-table-column label="Status" width="80" align="center">
                <template #default="{ row }">
                  <span :style="{ color: row.status === 'complete' ? '#67c23a' : '#f56c6c', fontSize:'16px' }">
                    {{ row.status === 'complete' ? '✓' : '♦' }}
                  </span>
                </template>
              </el-table-column>
            </el-table>
            <el-empty v-else description="No data" />
          </el-tab-pane>
        </el-tabs>

        <!-- 8. Cultivation Suggestions -->
        <div class="result-card" v-if="results.suggestions?.length" style="margin-top:24px;">
          <h3>Cultivation Suggestions</h3>
          <ol class="suggestions-list">
            <li v-for="(s, i) in results.suggestions" :key="i">{{ s }}</li>
          </ol>
        </div>

        <!-- 9. Run Log -->
        <el-collapse style="margin-top:20px;">
          <el-collapse-item title="Run Log" name="log">
            <pre class="run-log">{{ JSON.stringify(results.run_log, null, 2) }}</pre>
          </el-collapse-item>
        </el-collapse>

      </div>

      <!-- Citation -->
      <div class="citation-section" style="margin-top:40px;">
        <h3>Citation</h3>
        <p style="font-style: italic; color: #909399;">Coming soon</p>
        <p style="margin-top: 12px;">
          <strong>GitHub:</strong>
          <a href="https://github.com/rsj99-dev/MethArCT" target="_blank" rel="noopener">
            https://github.com/rsj99-dev/MethArCT
          </a>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { UploadFilled } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import axios from 'axios'

const jobId = ref(null)
const analyzing = ref(false)
const results = ref(null)
const pathwayTab = ref('methane')
const cultivationTab = ref('aa')
const fileList = ref([])

const beforeUpload = (file) => {
  const valid = ['.fa', '.fasta', '.faa']
  const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'))
  if (!valid.includes(ext)) {
    ElMessage.error('Please upload .fa / .fasta / .faa protein sequence file')
    return false
  }
  if (file.size / 1024 / 1024 > 50) {
    ElMessage.error('File size must not exceed 50 MB')
    return false
  }
  return true
}

const handleUploadSuccess = (res) => {
  if (res.status === 'success') {
    jobId.value = res.job_id
    ElMessage.success('File uploaded successfully')
  } else {
    ElMessage.error(`Upload failed: ${res.message || 'unknown error'}`)
  }
}

const handleUploadError = () => {
  ElMessage.error('File upload failed, please retry')
}

const startAnalysis = async () => {
  if (!jobId.value) return
  analyzing.value = true
  results.value = null
  try {
    const res = await axios.post(`/metharctapi/tools/metharct-beta/analyze/?job_id=${jobId.value}`)
    if (res.data.status === 'success') {
      results.value = res.data.results
      ElMessage.success('Comprehensive analysis complete')
    } else {
      ElMessage.error(`Analysis failed: ${res.data.message || 'unknown error'}`)
    }
  } catch (e) {
    ElMessage.error('Analysis request failed: ' + (e.response?.data?.detail || e.message))
  } finally {
    analyzing.value = false
  }
}

const resetAnalysis = () => {
  jobId.value = null
  analyzing.value = false
  results.value = null
  pathwayTab.value = 'methane'
  cultivationTab.value = 'aa'
  fileList.value = []
}
</script>

<style scoped>
.metharct-beta-container {
  padding: 100px 20px 40px;
  max-width: 1200px;
  margin: 0 auto;
}
.content-section h1 {
  font-size: 2rem;
  color: #4169E1;
  margin-bottom: 24px;
}
.description { margin-bottom: 24px; line-height: 1.7; }
.upload-section { margin-bottom: 32px; }
.results-section { margin-top: 32px; }

/* Result cards */
.result-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 20px 24px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.04);
}
.result-card h3 {
  font-size: 1.15rem;
  margin: 0 0 14px;
  color: #303133;
  border-bottom: 2px solid #4169E1;
  padding-bottom: 6px;
}

/* Assessment grid */
.assessment-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px 24px;
  margin-bottom: 12px;
}
.assess-item { font-size: 1rem; }
.assess-item .label { font-weight: 600; color: #606266; }

/* Metric row */
.metric-row {
  display: flex;
  gap: 32px;
  flex-wrap: wrap;
}
.metric-box { text-align: center; }
.metric-value {
  font-size: 1.8rem;
  font-weight: 700;
  color: #4169E1;
}
.metric-value.temp { color: #e6a23c; }
.metric-label { font-size: 0.85rem; color: #909399; margin-top: 4px; }

/* Suggestions */
.suggestions-list {
  padding-left: 20px;
  line-height: 2;
}
.suggestions-list li {
  margin-bottom: 4px;
  color: #303133;
}

/* Run log */
.run-log {
  background: #f5f7fa;
  padding: 12px;
  border-radius: 4px;
  font-size: 0.85rem;
  overflow-x: auto;
}

.aa-summary-bar { margin-bottom: 10px; font-weight: 600; }

.citation-section {
  border-top: 1px solid #ebeef5;
  padding-top: 20px;
  color: #606266;
  font-size: 0.9rem;
}
</style>
